import { parseAmount } from '../../../utils/formatRub'
import { getBudgetExpenseKind } from '../budgetExpenseVisuals'

export const ACT_FILTER_ALL = 'all'

function num(value, fallback = 0) {
  const parsed = parseAmount(value)
  return parsed == null ? fallback : parsed
}

function getActAmountAfterTax(act) {
  const afterTax = parseAmount(act?.amount_after_tax)
  return afterTax == null ? num(act?.amount) : afterTax
}

function getActAmountGross(act) {
  const gross = parseAmount(act?.amount)
  return gross == null ? getActAmountAfterTax(act) : gross
}

function getVatPercent(reportData) {
  return num(reportData?.vat_percent ?? reportData?.contract?.vat_percent, 5)
}

function amountAfterVat(amount, vatPercent) {
  const value = parseAmount(amount)
  if (value == null) return 0
  const divisor = 1 + (num(vatPercent, 5) / 100)
  return divisor ? Math.round((value / divisor) * 100) / 100 : value
}

function getStageBudgetAfterTax(reportData, stage, grossBudget) {
  const explicit = parseAmount(stage?.cost_after_tax ?? stage?.cost_no_vat ?? stage?.amount_without_vat)
  if (explicit != null) return explicit
  return amountAfterVat(grossBudget, getVatPercent(reportData))
}

export function formatMoney(value, { empty = '0 ₽' } = {}) {
  const parsed = parseAmount(value)
  if (parsed == null) return empty
  return `${new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parsed).replace(/\u00a0/g, ' ')} ₽`
}

export function formatPercent(value) {
  const parsed = parseAmount(value)
  if (parsed == null) return '0%'
  if (parsed === 100) return '100%'
  return `${new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(parsed).replace(/\u00a0/g, ' ')}%`
}

export function formatDateShort(dateValue) {
  if (!dateValue) return '—'
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '—'
  const [last, first] = parts
  return `${last?.[0] || ''}${first?.[0] || ''}`.toUpperCase()
}

export function getDepartmentTone(department) {
  const text = String(department || '').toLowerCase()
  if (text.includes('ар') || text.includes('арх')) return 'arch'
  if (text.includes('кж') || text.includes('км') || text.includes('инж') || text.includes('гип')) return 'eng'
  if (text.includes('диз')) return 'design'
  if (text.includes('бух') || text.includes('фин') || text.includes('налог')) return 'fin'
  if (text.includes('торг') || text.includes('прод')) return 'sales'
  if (text.includes('пред') || text.includes('управ') || text.includes('выпуск')) return 'mgmt'
  return 'default'
}

export function getContractStatusVariant(status) {
  const normalized = String(status || '').toLowerCase().trim()
  if (normalized === 'в работе' || normalized === 'active' || normalized === 'действующий') return 'active'
  if (normalized.includes('заверш') || normalized.includes('согласован')) return 'success'
  if (normalized.includes('соглас')) return 'review'
  if (normalized.includes('приост') || normalized.includes('отмен')) return 'warning'
  return 'draft'
}

export function getBudgetStatusLabel(status) {
  const normalized = String(status || 'новый').toLowerCase().trim()
  if (normalized === 'согласован') return 'Согласован'
  if (normalized === 'в согласовании') return 'В согласовании'
  return 'Новый'
}

export function getBudgetStatusVariant(status) {
  const normalized = String(status || '').toLowerCase().trim()
  if (normalized === 'согласован') return 'success'
  if (normalized === 'в согласовании') return 'review'
  return 'draft'
}

export function buildActFilterOptions(acts = []) {
  const visibleActs = (acts || []).filter(Boolean)
  const total = visibleActs.reduce((sum, act) => sum + getActAmountGross(act), 0)
  return [
    {
      id: ACT_FILTER_ALL,
      label: 'Все акты',
      meta: `${visibleActs.length} ${pluralizeActs(visibleActs.length)} · ${formatMoney(total)}`,
      amount: total,
    },
    ...visibleActs.map((act, index) => {
      const amount = getActAmountGross(act)
      const number = String(act.number || index + 1).trim()
      const date = formatDateShort(act.issue_date)
      return {
        id: String(act.id || number || index),
        act,
        label: `Акт №${number}`,
        headerBadge: date === '—' ? `№${number}` : `№${number} от ${date}`,
        meta: `${date} · ${formatMoney(amount)}`,
        amount,
      }
    }),
  ]
}

function pluralizeActs(count) {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return 'акт'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'акта'
  return 'актов'
}

function getStage(reportData) {
  return (reportData?.stages || [])[0] || {}
}

function getStageBlock(reportData) {
  const stage = getStage(reportData)
  const stageId = String(stage.id || stage.type || '').trim()
  return (reportData?.stages_income || []).find((block) => {
    const blockId = String(block.stage_id || block.stage_name || '').trim()
    return blockId === stageId || (stageId && blockId.includes(stageId))
  }) || (reportData?.stages_income || [])[0] || {}
}

function getTotalActuated(reportData) {
  const acts = reportData?.acts || []
  if (Array.isArray(reportData?.acts_in_period)) {
    return acts.reduce((sum, act) => sum + getActAmountAfterTax(act), 0)
  }
  if (acts.length && acts.every((act) => parseAmount(act?.amount_after_tax) != null)) {
    return acts.reduce((sum, act) => sum + getActAmountAfterTax(act), 0)
  }
  const stage = getStage(reportData)
  const fromStage = num(stage.actuated_in_year) + num(stage.prev_actuated_calculated ?? stage.prev_actuated_amount)
  if (fromStage > 0) return fromStage
  return 0
}

function getTotalActuatedGross(reportData) {
  return (reportData?.acts || []).reduce((sum, act) => sum + getActAmountGross(act), 0)
}

function scaleForSelectedAct(reportData, selectedActId) {
  if (!selectedActId || selectedActId === ACT_FILTER_ALL) return 1
  const total = getTotalActuated(reportData)
  if (!total) return 0
  const act = (reportData?.acts || []).find((item) => String(item.id || item.number) === String(selectedActId))
  if (!act) return 1
  return getActAmountAfterTax(act) / total
}

function getSelectedActAmount(reportData, selectedActId) {
  if (!selectedActId || selectedActId === ACT_FILTER_ALL) return getTotalActuated(reportData)
  const act = (reportData?.acts || []).find((item) => String(item.id || item.number) === String(selectedActId))
  return act ? getActAmountAfterTax(act) : 0
}

function getActTaxDelta(act) {
  if (parseAmount(act?.amount_after_tax) == null) return 0
  return Math.max(getActAmountGross(act) - getActAmountAfterTax(act), 0)
}

function getSelectedActTaxDelta(reportData, selectedActId) {
  if (!selectedActId || selectedActId === ACT_FILTER_ALL) {
    return (reportData?.acts || []).reduce((sum, act) => sum + getActTaxDelta(act), 0)
  }
  const act = (reportData?.acts || []).find((item) => String(item.id || item.number) === String(selectedActId))
  return act ? getActTaxDelta(act) : 0
}

function getOtherExpenseActuatedAmount(expense, selectedActAmount, selectedActTaxDelta) {
  const percentage = num(expense.percentage)
  const baseAmount = selectedActAmount * percentage / 100
  if (getExpenseRowType(expense) !== 'tax') {
    return Math.round(baseAmount * 100) / 100
  }
  return Math.round((baseAmount + selectedActTaxDelta) * 100) / 100
}

function getExpenseActuatedForPaymentTypes(expense, reportData, selectedActId) {
  const acts = (reportData.acts || []).filter(act => !selectedActId || selectedActId === ACT_FILTER_ALL || String(act.id || act.number) === String(selectedActId))
  if (!acts.length) return 0
  if (acts.every(act => act.distribution_eligible !== false && (!act.payment_type || act.payment_type === 'bank_transfer'))) return getOtherExpenseActuatedAmount(expense, getSelectedActAmount(reportData, selectedActId), getSelectedActTaxDelta(reportData, selectedActId))
  const expenseKind = getBudgetExpenseKind(expense)
  return roundMoney(acts.reduce((sum, act) => {
    if (act.distribution_eligible === false || act.payment_type === 'artel') return sum
    if (act.payment_type === 'cash' && !['production', 'non-production'].includes(expenseKind)) return sum
    return sum + getOtherExpenseActuatedAmount(expense, getActAmountAfterTax(act), getActTaxDelta(act))
  }, 0))
}

function getEmployeeActuatedAmount(employee, selectedActId) {
  if (!selectedActId || selectedActId === ACT_FILTER_ALL) {
    const hasReceivedValue = employee.received != null || employee.previously_act_amount != null || employee.acts_income != null
    return hasReceivedValue
      ? num(employee.received ?? (num(employee.previously_act_amount) + num(employee.acts_income)))
      : 0
  }
  const actsByAct = employee.acts_by_act || {}
  return num(actsByAct[String(selectedActId)])
}

function getDepartmentSection(department) {
  const type = String(department?.department_type || '').trim()
  if (type === 'Производственное') return 'production'
  const explicit = String(department?.section || department?.group || '').toLowerCase().trim()
  if (explicit.includes('production') || explicit.includes('производ')) return 'production'
  return 'service'
}

function getSectionLabel(section) {
  return section === 'production' ? 'Производство' : 'Обслуживание'
}

function getSectionTotalLabel(section) {
  return section === 'production' ? 'Итого производство' : 'Итого обслуживание'
}

// Порог реконсиляции округления: суммарный копеечный дрейф от построчного округления
// (например, 4 199 999 вместо 4 200 000) досаживается в последнюю денежную строку.
const ROUNDING_RECONCILE_THRESHOLD = 2

function roundMoney(value) {
  return Math.round(Number(value || 0) * 100) / 100
}

function pushSectionRow(rows, sectionState, section) {
  if (sectionState.current === section) return
  sectionState.current = section
  rows.push({
    key: `section-${section}`,
    type: 'section',
    label: getSectionLabel(section),
    section,
  })
}

function getExpenseRowType(expense) {
  if (getBudgetExpenseKind(expense) === 'tax') return 'tax'
  return 'expense'
}

function uniqueEmployeeNames(employees) {
  const seen = new Set()
  return employees
    .map((employee) => String(employee.employee_name || '').trim())
    .filter((name) => {
      if (!name || seen.has(name)) return false
      seen.add(name)
      return true
    })
}

function isTeamLeadEmployee(employee) {
  return employee?.is_team_lead === true || employee?.is_team_lead === 1 || String(employee?.is_team_lead || '').toLowerCase() === 'true'
}

function uniqueTeamLeadNames(employees) {
  return uniqueEmployeeNames(employees.filter(isTeamLeadEmployee))
}

function getTeamBudgetAmount(employees, team = {}) {
  const employeesAmount = employees.reduce((sum, employee) => sum + num(employee.income), 0)
  if (employees.length > 0 || employeesAmount > 0) return employeesAmount
  return num(team.total_income ?? team.totalIncome ?? team.amount)
}

function getTeamActuatedAmount(employees, selectedActId, team = {}) {
  const employeesAmount = employees.reduce((sum, employee) => sum + getEmployeeActuatedAmount(employee, selectedActId), 0)
  if (employees.length > 0 || employeesAmount > 0) return employeesAmount
  return num(team.total_received ?? team.totalReceived ?? team.received)
}

export function getEmployeeSubTeam(employee) {
  return String(employee?.sub_team || '').trim()
}

export function collectSubTeamsFromEmployees(employees = []) {
  const tags = new Set()
  employees.forEach((employee) => {
    const tag = getEmployeeSubTeam(employee)
    if (tag) tags.add(tag)
  })
  return [...tags].sort((a, b) => a.localeCompare(b, 'ru'))
}

export function teamUsesSubTeams(employees = []) {
  return collectSubTeamsFromEmployees(employees).length > 0
}

function pluralizeSubTeams(count) {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return 'подкоманда'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'подкоманды'
  return 'подкоманд'
}

function buildSubTeamEmployeeLabel(employees) {
  const contributors = employees.filter((employee) => (
    num(employee?.income) > 0 || num(employee?.received) > 0
  ))
  const names = uniqueEmployeeNames(contributors.length ? contributors : employees)
  if (names.length) return names.join(', ')
  return '—'
}

function pushTeamBudgetRows(rows, {
  department,
  team,
  employees,
  selectedActId,
  rowIndex,
  planBase,
  actuatedBase,
  useActualAllocations = false,
}) {
  const sortedEmployees = [...employees].sort((a, b) =>
    String(a.employee_name || '').localeCompare(String(b.employee_name || ''), 'ru')
  )
  const hasExplicitPercentage = Object.prototype.hasOwnProperty.call(team, 'percentage')
  const usesRuleBase = hasExplicitPercentage && sortedEmployees.length > 0 && planBase > 0
  const employeeBudgetAmount = getTeamBudgetAmount(sortedEmployees, team)
  const employeeActuatedAmount = Math.round(getTeamActuatedAmount(sortedEmployees, selectedActId, team) * 100) / 100
  const budgetAmount = usesRuleBase && planBase > 0
    ? roundMoney(planBase * num(team.percentage) / 100)
    : employeeBudgetAmount
  const actuatedAmount = !useActualAllocations && usesRuleBase && actuatedBase > 0
    ? roundMoney(actuatedBase * num(team.percentage) / 100)
    : employeeActuatedAmount
  const teamKey = `team-${department.department_name}-${team.team_id || team.team_name || rowIndex}`
  const subTeams = collectSubTeamsFromEmployees(sortedEmployees)

  if (!subTeams.length) {
    rows.push({
      key: teamKey,
      type: 'team',
      section: getDepartmentSection(department),
      label: team.team_name || department.department_name || 'Без команды',
      departmentName: department.department_name || '',
      employeeName: uniqueTeamLeadNames(sortedEmployees).join(', ') || '—',
      budgetAmount,
      actuatedAmount,
      percentage: hasExplicitPercentage ? num(team.percentage) : null,
    })
    return
  }

  rows.push({
    key: teamKey,
    type: 'team-group',
    section: getDepartmentSection(department),
    label: team.team_name || department.department_name || 'Без команды',
    departmentName: department.department_name || '',
    employeeName: `${subTeams.length} ${pluralizeSubTeams(subTeams.length)}`,
    budgetAmount,
    actuatedAmount,
    percentage: hasExplicitPercentage ? num(team.percentage) : null,
    subTeamCount: subTeams.length,
    expandable: true,
  })

  const subTeamValues = subTeams.map((subTeam) => {
    const subEmployees = sortedEmployees.filter((employee) => getEmployeeSubTeam(employee) === subTeam)
    return {
      subTeam,
      subEmployees,
      budgetAmount: getTeamBudgetAmount(subEmployees),
      actuatedAmount: Math.round(getTeamActuatedAmount(subEmployees, selectedActId) * 100) / 100,
    }
  })
  const subTeamBudgetBase = subTeamValues.reduce((sum, item) => sum + item.budgetAmount, 0)
  const subTeamActuatedBase = subTeamValues.reduce((sum, item) => sum + item.actuatedAmount, 0)
  let allocatedSubTeamBudget = 0
  let allocatedSubTeamActuated = 0

  subTeamValues.forEach((item, index) => {
    const isLastSubTeam = index === subTeamValues.length - 1
    let subBudgetAmount = usesRuleBase && subTeamBudgetBase > 0
      ? roundMoney(budgetAmount * item.budgetAmount / subTeamBudgetBase)
      : item.budgetAmount
    let subActuatedAmount = !useActualAllocations && usesRuleBase && actuatedBase > 0 && subTeamActuatedBase > 0
      ? roundMoney(actuatedAmount * item.actuatedAmount / subTeamActuatedBase)
      : item.actuatedAmount
    if (usesRuleBase && isLastSubTeam) {
      subBudgetAmount = roundMoney(budgetAmount - allocatedSubTeamBudget)
      subActuatedAmount = roundMoney(actuatedAmount - allocatedSubTeamActuated)
    }
    allocatedSubTeamBudget = roundMoney(allocatedSubTeamBudget + subBudgetAmount)
    allocatedSubTeamActuated = roundMoney(allocatedSubTeamActuated + subActuatedAmount)
    const subPercentage = hasExplicitPercentage
      ? (budgetAmount > 0 ? num(team.percentage) * subBudgetAmount / budgetAmount : 0)
      : null
    rows.push({
      key: `${teamKey}-sub-${item.subTeam}`,
      type: 'sub-team',
      parentKey: teamKey,
      section: getDepartmentSection(department),
      label: item.subTeam,
      departmentName: department.department_name || '',
      employeeName: buildSubTeamEmployeeLabel(item.subEmployees),
      budgetAmount: subBudgetAmount,
      actuatedAmount: subActuatedAmount,
      percentage: subPercentage,
    })
  })
}

// Вставляет строки «Итого по разделу» (Производство/Обслуживание) после строк раздела.
// Подкоманды не учитываются — их суммы уже входят в родительскую строку team-group.
function insertSectionTotalRows(rows, totalBudget) {
  const amountRowTypes = ['team', 'team-group', 'expense', 'tax']
  const result = []
  let sectionAgg = null

  const flushSection = () => {
    if (!sectionAgg) return
    result.push({
      key: `section-total-${sectionAgg.section}`,
      type: 'section-total',
      section: sectionAgg.section,
      label: getSectionTotalLabel(sectionAgg.section),
      budgetAmount: roundMoney(sectionAgg.budget),
      actuatedAmount: roundMoney(sectionAgg.actuated),
      percentage: Math.round(sectionAgg.percent * 100) / 100,
    })
    sectionAgg = null
  }

  rows.forEach((row) => {
    if (row.type === 'section') {
      flushSection()
      result.push(row)
      sectionAgg = { section: row.section, budget: 0, actuated: 0, percent: 0 }
      return
    }
    result.push(row)
    if (!sectionAgg || !amountRowTypes.includes(row.type)) return
    sectionAgg.budget += row.budgetAmount
    sectionAgg.actuated += row.actuatedAmount
    // Как в таблице: команды без явного процента получают долю от общего бюджета
    sectionAgg.percent += row.percentage != null
      ? num(row.percentage)
      : (totalBudget > 0 ? (row.budgetAmount / totalBudget) * 100 : 0)
  })
  flushSection()

  rows.splice(0, rows.length, ...result)
}

export function buildStageBudgetModel(reportData, selectedActId = ACT_FILTER_ALL) {
  const block = getStageBlock(reportData)
  const rows = []
  const stage = getStage(reportData)
  const stageBudget = num(stage.cost)
  const stageBudgetAfterTax = getStageBudgetAfterTax(reportData, stage, stageBudget)
  const selectedActAmount = getSelectedActAmount(reportData, selectedActId)
  const selectedActTaxDelta = getSelectedActTaxDelta(reportData, selectedActId)
  const sectionState = { current: null }

  const departments = [...(block.departments || [])]

  departments.forEach((department) => {
    pushSectionRow(rows, sectionState, getDepartmentSection(department))
    const teams = [...(department.teams || [])]
    teams.forEach((team, teamIndex) => {
      const employees = [...(team.employees || [])]
      pushTeamBudgetRows(rows, {
        department,
        team,
        employees,
        selectedActId,
        rowIndex: teamIndex,
        planBase: stageBudgetAfterTax,
        actuatedBase: (reportData.acts || []).some(act => act.distribution_eligible === false)
          ? (reportData.acts || []).filter(act => act.distribution_eligible !== false && (selectedActId === ACT_FILTER_ALL || String(act.id || act.number) === String(selectedActId))).reduce((sum, act) => sum + getActAmountAfterTax(act), 0)
          : selectedActAmount,
        useActualAllocations: (reportData.acts || []).some(act => act.payment_type && act.payment_type !== 'bank_transfer'),
      })
    })
  })

  const otherExpenses = block.other_expenses || []
  if (otherExpenses.length) {
    pushSectionRow(rows, sectionState, 'service')
    otherExpenses.forEach((expense, index) => {
      const budgetAmount = num(expense.amount)
      const percentage = num(expense.percentage)
      const type = getExpenseRowType(expense)
      const expenseKind = getBudgetExpenseKind(expense)
      rows.push({
        key: `other-${expense.expense_type || expense.label || index}`,
        type,
        expenseKind,
        section: 'service',
        label: expense.expense_type || expense.label || 'Остальная статья',
        teamName: '',
        employeeName: '',
        budgetAmount,
        actuatedAmount: getExpenseActuatedForPaymentTypes(expense, reportData, selectedActId),
        percentage,
      })
    })
  }

  const amountRowTypes = ['team', 'team-group', 'expense', 'tax']
  let totalBudget = rows.reduce((sum, row) => sum + (amountRowTypes.includes(row.type) ? row.budgetAmount : 0), 0)
  let totalActuated = rows.reduce((sum, row) => sum + (amountRowTypes.includes(row.type) ? row.actuatedAmount : 0), 0)

  // Реконсиляция округления: остаток уходит в последнюю денежную строку,
  // чтобы итоги по бюджету и по факту совпадали с суммами стадии и актов.
  const donorRow = [...rows].reverse().find((row) => ['tax', 'expense', 'team'].includes(row.type))
  const roundingDrift = roundMoney(stageBudget - totalBudget)
  if (donorRow && stageBudget > 0 && totalBudget > 0 && roundingDrift !== 0 && Math.abs(roundingDrift) <= ROUNDING_RECONCILE_THRESHOLD) {
    donorRow.budgetAmount = roundMoney(donorRow.budgetAmount + roundingDrift)
    totalBudget = stageBudget
  }

  const actuatedGrossTarget = selectedActId && selectedActId !== ACT_FILTER_ALL
    ? selectedActAmount + selectedActTaxDelta
    : getTotalActuatedGross(reportData)
  const actuatedRoundingDrift = roundMoney(actuatedGrossTarget - totalActuated)
  if (donorRow && actuatedGrossTarget > 0 && totalActuated > 0 && actuatedRoundingDrift !== 0 && Math.abs(actuatedRoundingDrift) <= ROUNDING_RECONCILE_THRESHOLD) {
    donorRow.actuatedAmount = roundMoney(donorRow.actuatedAmount + actuatedRoundingDrift)
    totalActuated = actuatedGrossTarget
  }

  insertSectionTotalRows(rows, totalBudget)

  rows.push({
    key: 'total',
    type: 'total',
    label: 'ОБЩАЯ СУММА',
    budgetAmount: totalBudget,
    actuatedAmount: totalActuated,
    percentage: 100,
  })

  const resolvedStageBudget = stageBudget || totalBudget
  const stageActuated = getTotalActuated(reportData) || totalActuated
  const stageActuatedGross = getTotalActuatedGross(reportData) || stageActuated
  const afterTaxActuated = selectedActId && selectedActId !== ACT_FILTER_ALL ? selectedActAmount : stageActuated
  rows.push({
    key: 'total-after-tax',
    type: 'after-tax-total',
    label: 'ИТОГО после вычета налогов',
    budgetAmount: stageBudgetAfterTax,
    actuatedAmount: afterTaxActuated,
    percentage: 100,
  })
  const remaining = Math.max(resolvedStageBudget - stageActuatedGross, 0)

  return {
    rows,
    stage,
    stageBlock: block,
    totalBudget,
    totalActuated,
    stageBudget: resolvedStageBudget,
    stageBudgetAfterTax,
    stageActuated,
    stageActuatedGross,
    remaining,
    actScale: scaleForSelectedAct(reportData, selectedActId),
  }
}
